import { useState, useContext } from 'react';
import { format, parseISO } from 'date-fns';
import { MdOutlineCheckCircle } from 'react-icons/md';
import { LuCircleEllipsis } from 'react-icons/lu';
import AgreementsDrawer from '../../CampaignDetail/drawers/AgreementsDrawer';
import { CampaignContext } from '~/context/CampaignProvider';

interface Props {
    campaignRequest: any;
    patient: any;
    metadata: any;
    application: any;
    campaignApproval?: any; // Campaign approval by HHL staff (NOT used currently)
    onCampaignRequestUpdate?: (updatedCampaignRequest: any) => void;
    completionStatus?: any;
    onDataSaved?: () => void;
}

const AgreementsTab: React.FC<Props> = ({
    campaignRequest,
    patient,
    metadata,
    application,
    campaignApproval,
    onCampaignRequestUpdate,
    completionStatus,
    onDataSaved,
}) => {
    const contextValue = useContext(CampaignContext);
    if (!contextValue) throw new Error('CampaignContext not found');

    const [state, campaignDispatch] = contextValue as unknown as [
        { campaignData: any },
        any
    ];
    const campaignData = state.campaignData;

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // Check completion status for agreements section
    const agreementsSection = completionStatus?.sections?.find(
        (s: any) => s.key === 'agreement'
    );
    const isAgreementsComplete = agreementsSection?.is_complete ?? false;

    return (
        <>
            <div className='mb-7.5'>
                <div className='flex items-center justify-between'>
                    <div>
                        <h3
                            className={`mb-2 text-[16px]/[24px] font-medium uppercase ${
                                isAgreementsComplete
                                    ? 'text-[#AFC12D]'
                                    : 'text-[#FEBF3E]'
                            }`}
                        >
                            {isAgreementsComplete ? (
                                <MdOutlineCheckCircle
                                    className='me-2'
                                    size={24}
                                />
                            ) : (
                                <LuCircleEllipsis className='me-2' size={24} />
                            )}
                            {isAgreementsComplete ? 'Complete' : 'Pending'}
                        </h3>
                        <h2 className='text-[22px]/[28px] font-bold'>
                            Terms & Agreements
                        </h2>
                    </div>
                    <button
                        className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        onClick={() => setIsDrawerOpen(true)}
                    >
                        Edit
                    </button>
                </div>

                <div>
                    <h3 className='mb-3 text-[14px]/[18px] font-semibold'>
                        Certifications & Agreements
                    </h3>

                    <div className='mb-[16px]'>
                        <label className='text-[12px]/[16px] text-[#949599]'>
                            The information contained in this application
                            will be used by Help Hope Live to confirm
                            medical and financial need. I certify that the
                            information contained herein is true to the best
                            of my knowledge, information, and belief.
                        </label>
                        <div>
                            {application?.ag_certify_info
                                ? '✓ Yes'
                                : '✗ No'}
                        </div>
                    </div>

                    <div className='mb-[16px]'>
                        <label className='text-[12px]/[16px] text-[#949599]'>
                            A medical professional must provide information
                            confirming my medical need before Help Hope Live
                            can process any fund requests I submit.
                        </label>
                        <div>
                            {application?.ag_medpro_required
                                ? '✓ Yes'
                                : '✗ No'}
                        </div>
                    </div>

                    <div className='mb-[16px]'>
                        <label className='text-[12px]/[16px] text-[#949599]'>
                            I agree to the Help Hope Live Terms of Service
                            and will make sure my fundraising volunteers
                            understand and adhere to the guidelines set
                            forth in these terms.
                        </label>
                        <div>
                            {application?.ag_terms_of_service
                                ? '✓ Yes'
                                : '✗ No'}
                        </div>
                    </div>

                    <div className='mb-[16px]'>
                        <label className='text-[12px]/[16px] text-[#949599]'>
                            Because I am working with and using materials
                            provided to me by a 501(c)(3) nonprofit, all
                            funds raised for Help Hope Live must come to
                            Help Hope Live. Failure to send all funds to
                            Help Hope Live will negate the tax deductibility
                            of donations. This may have legal and/or tax
                            implications for me and my campaign
                            contributors. Funds given directly to me are not
                            considered tax deductible to the campaign
                            contributor. I will ensure that my fundraising
                            volunteers understand this.
                        </label>
                        <div>
                            {application?.ag_understand_funds_come_to_hhl
                                ? '✓ Yes'
                                : '✗ No'}
                        </div>
                    </div>

                    <div className='mb-[16px]'>
                        <label className='text-[12px]/[16px] text-[#949599]'>
                            Photographs and video on my Help Hope Live
                            Campaign Page may be used by Help Hope Live for
                            media outreach, promotional materials, web-based
                            fundraising, and any other efforts my Help Hope
                            Live Client Services Coordinator may deem
                            necessary to maximize the funds raised in my
                            honor. I understand that the information
                            disclosed may include facts about my general
                            medical condition, financial need, and progress
                            of my fundraising campaign.
                        </label>
                        <div>
                            {application?.ag_understand_hhl_may_use_photos
                                ? '✓ Yes'
                                : '✗ No'}
                        </div>
                    </div>

                    <div className='mb-[16px]'>
                        <label className='text-[12px]/[16px] text-[#949599]'>
                            Once my application has been approved, Help Hope
                            Live will publish information about my personal
                            health story and fundraising efforts online via
                            a customizable campaign page at
                            www.helphopelive.org, unless privacy is
                            otherwise specified.
                        </label>
                        <div>
                            {application?.ag_understand_hhl_publishes_health_info
                                ? '✓ Yes'
                                : '✗ No'}
                        </div>
                    </div>

                    <div className='mb-[16px]'>
                        <label className='text-[12px]/[16px] text-[#949599]'>
                            (Optional) To use any public content on my Help
                            Hope Live Campaign Page, including but not
                            limited to photos, videos, and updates, to help
                            spread the nonprofit mission. I agree that this
                            public content may be used by Help Hope Live for
                            promotional content including brochures, print
                            and digital newsletters, social media posts, and
                            annual reports. This optional consent will help
                            us reach and support more families facing a
                            medical crisis.
                        </label>
                        <div>
                            {application?.ag_understand_hhl_may_use_public_content
                                ? '✓ Yes'
                                : '✗ No'}
                        </div>
                    </div>

                    <hr className='my-6 border-gray-200' />

                    <h3 className='mb-3 text-[14px]/[18px] font-semibold'>
                        Signature
                    </h3>

                    <div className='flex gap-8'>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Signed by
                            </label>
                            <div>{application?.ag_signature || ''}</div>
                        </div>

                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Date
                            </label>
                            <div>
                                {application?.ag_signature_date
                                    ? format(
                                          parseISO(
                                              application.ag_signature_date
                                          ),
                                          'PPP'
                                      )
                                    : ''}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <AgreementsDrawer
                isOpen={isDrawerOpen}
                setIsOpen={setIsDrawerOpen}
                application={application}
                applicationId={application?.id}
                onUpdate={(response) => {
                    console.log('Agreements updated:', response);
                    // The response includes complete campaign data, update context directly
                    const {
                        campaign: updatedCampaign,
                        campaign_id,
                        patient: updatedPatient,
                        csc: updatedCsc,
                        campaign_request: updatedCampaignRequest,
                        notes: updatedNotes,
                        coordinator: updatedCoordinator,
                        campaign_approval: updatedCampaignApproval,
                        financial_coordinator: updatedFinancialCoordinator,
                        ...applicationFields
                    } = response;

                    // Add campaign ID back to application object
                    const updatedApplication = {
                        ...applicationFields,
                        campaign: campaign_id,
                    };

                    // Update the context with the new data
                    campaignDispatch({
                        type: 'set_campaign',
                        payload: {
                            campaign: updatedCampaign,
                            patient: updatedPatient,
                            csc: updatedCsc,
                            campaign_request: updatedCampaignRequest,
                            notes: updatedNotes,
                            campaignPhotoInfo:
                                campaignData?.campaignPhotoInfo || null,
                            coordinator: updatedCoordinator,
                            financial_coordinator:
                                updatedFinancialCoordinator ||
                                campaignData?.financial_coordinator ||
                                null,
                            count: campaignData?.count || 0,
                            application: updatedApplication,
                            campaign_approval: updatedCampaignApproval,
                        },
                    });

                    // Refresh completion status
                    if (onDataSaved) {
                        onDataSaved();
                    }
                }}
            />
        </>
    );
};

export default AgreementsTab;
